import {gql} from '@apollo/client';

import {doQueryWithoutCache} from 'graphql-client/ApolloWrappers';
import {
  AntibodyDomainsQuery,
  AntibodyDomainsQuery_antibody_AntibodyNode_domains_AntibodyDomainNode as AntibodyDomainNode,
  AntibodyDomainsQueryVariables,
} from 'molbio/adcs/conjugation-sites/__generated/AntibodyDomainsQuery';

/** An antibody domain with its sequence and antibody numbering, as needed to find and label its sites. */
export type AntibodyDomain = AntibodyDomainNode;

export const ANTIBODY_DOMAINS_QUERY = gql`
  query AntibodyDomainsQuery($antibodyId: StrictString!) @skipAudit @engComponent(name: MOLBIO) {
    antibody(antibodyId: $antibodyId) {
      ... on AntibodyNode {
        id
        domains {
          label
          entity {
            ... on AaSequenceNode {
              id
              experimentalCurrentVersion {
                dbId
                aminoAcids
                antibodyNumberingInfo {
                  numbering
                }
              }
            }
          }
        }
      }
    }
  }
`;

/** The antibody's domains, or null if the antibody is inaccessible. */
export function getAntibodyDomains({antibody}: AntibodyDomainsQuery): Array<AntibodyDomain> | null {
  return antibody.__typename === 'AntibodyNode' ? antibody.domains : null;
}

/**
 * Fetches an antibody's domains with their sequences and antibody numbering, bypassing the Apollo cache so
 * conjugation sites found from them reflect the domains' current sequences.
 *
 * @param antibodyId The API ID of the antibody
 * @returns The antibody's domains, or null if the antibody is inaccessible
 */
export async function fetchAntibodyDomains(antibodyId: string): Promise<Array<AntibodyDomain> | null> {
  return getAntibodyDomains(
    await doQueryWithoutCache<AntibodyDomainsQuery, AntibodyDomainsQueryVariables>(
      {query: ANTIBODY_DOMAINS_QUERY},
      {variables: {antibodyId}}
    )
  );
}
